<template>
	<LayoutScreen background="--background-base" max-width="500px">
		<template #header>
			<LayoutFlex padding="0 var(--size-20)" gap="0">
				<CancelHeader />
			</LayoutFlex>
		</template>

		<template #content>
			<!-- Figma 7415:18138: the title 24 under the logo row; title, card and list 20 apart; the button in
			     the page 24 under the list. -->
			<LayoutFlex padding="var(--size-24) var(--size-20)" gap="var(--size-24)">
				<LayoutFlex padding="0" gap="var(--size-20)" align-items="center">
					<Text font="--heading-h3" color="--text-dark-primary" text-align="center">
						Here’s what happens if you cancel right now
					</Text>

					<BreakdownCard :rows="rows">
						Your {{ money(q.monthlyPrice) }}/month rate is based on a {{ q.termMonths }}-month commitment. If you end
						your plan early, the medication you’ve already received is priced at the regular
						{{ breakdown ? `${money(breakdown.rate)}/month` : 'monthly' }} rate.
					</BreakdownCard>

					<ConsequenceList :items="consequences" />
				</LayoutFlex>

				<Button full-width :disabled="processing || !ready" @ds:click="onPay">
					<Text font="--text-16-18-semi">
						{{
							refundToday > 0
								? `Cancel and get ${money(refundToday)} refund`
								: `Cancel and pay ${money(q.endToday?.amount)}`
						}}
					</Text>
				</Button>
			</LayoutFlex>
		</template>
	</LayoutScreen>

	<!-- "Processing your payment…" takeover (Figma 6455:21838) — an overlay, not a route: the page
	     must stay mounted while the charge is in flight. -->
	<Modal :visible="processing" fullscreen animation="none">
		<VerifyingScreen
			title="Processing your payment..."
			hint="Please don't go back or refresh the page while we process your transaction"
			title-width="272px"
		/>
	</Modal>
</template>

<script setup lang="ts">
// /cancel/commitment/end-now — Figma `kova cancel flow6` 6582:15236, now 7415:18138 (+ the 2/3- and 3/3-payment
// variants): pay the exit fee today and end the plan. Also the target of "Pay $X and cancel today"
// on /status, after "Keep my plan" or the instalments.
//
// Reads `instant_fee`, re-read on entry: the line items come from its `paid` / `shipped_months` / `exit_rate`,
// the access line from its `access_until`. A cycle paid past what shipped (a refill in review: its charge is
// counted, its supply not yet) comes as `amount: 0` + `refund` — "Your refund" and "Cancel and get $X refund",
// as on the paid-in-full screen. Errors: a declined card → the failure screen; a buy-out no longer offered →
// re-read and back to the options; a lost answer → re-read, and the receipt if the fee was taken.
//
// Analytics (AL-15098): CANCEL_RIGHT_NOW_SUMMARY with `data.commitment`, closed with `cancel_and_pay` on
// the tap, then PROCESSING_PAYMENT around the charge; a paid fee is a `hard` SubscriptionStateChanged.
//
// Not handled, as billing can't send it (checked 2026-10-22): 3DS (a recurring charge of the saved card;
// `requires_action`, should it come, goes to the failure screen).
import { computed, onMounted, ref } from 'vue'
import { onBeforeRouteLeave, useRouter } from 'vue-router'
import { Button, LayoutFlex, LayoutScreen, Modal, Text } from '@simple-life-app/growth-ui-kova'
import CancelHeader from '@/components/CancelHeader.vue'
import BreakdownCard, { type BreakdownRow } from '@/components/cancel/BreakdownCard.vue'
import ConsequenceList from '@/components/cancel/ConsequenceList.vue'
import VerifyingScreen from '@/components/payment/VerifyingScreen.vue'
import { CancellationNotOfferedError, PaymentDeclinedError } from '@/api/medBilling'
import { COMMITMENT_ROUTES, feeBreakdown, feeTaken } from '@/cancelFlow/commitment'
import { formatDate, formatMoney, pluralize } from '@/cancelFlow/format'
import { useCancelFlowStore } from '@/store/cancelFlow'
import { useSubscriptionStore } from '@/store/subscription'
import { useSurveyStore } from '@/store/survey'
import {
	STEP_TITLE,
	commitmentStepData,
	processingPaymentStep,
	stateChangeOnCancel,
	useCancelStepLogging,
} from '@/analytics'

const router = useRouter()
const flow = useCancelFlowStore()
const subs = useSubscriptionStore()
const survey = useSurveyStore()

const q = computed(() => flow.quotes)
const processing = ref(false)
/** The fee re-read on entry has settled — billing prices the charge at the call, from what it holds then. */
const ready = ref(false)
/**
 * The charge is in flight: leaving would let a second screen send a second pay-fee, and billing takes
 * both (no idempotency key, no lock). Not `processing`, which stays up through our own navigation.
 */
let charging = false

// Entered from /status ("Pay $X and cancel today", read at app start) or "Try again": the store can be
// days old, and a renewal or an approved supply since moves the fee. Re-read, so the button, the line
// items and the receipt show what billing will charge; gone → the options show what is offered now.
onMounted(async () => {
	await subs.refreshSubscription()
	ready.value = true
	if (!q.value.endToday) router.replace(COMMITMENT_ROUTES.options)
})

onBeforeRouteLeave(() => !charging)

/** The cycle paid past what shipped (a refill in review): `amount: 0` + `refund` — ending today pays it back. */
const refundToday = computed(() => q.value.endToday?.refund ?? 0)

function money(amount: number | null | undefined) {
	return formatMoney(amount, q.value.currency)
}

const breakdown = computed(() => feeBreakdown(q.value.endToday, q.value.monthlyPrice))

const step = useCancelStepLogging(STEP_TITLE.CANCEL_RIGHT_NOW_SUMMARY, {
	getCommitment: () => {
		const fee = q.value.endToday
		return commitmentStepData(subs.subscription, flow.kind, fee, { fee: fee?.amount ?? 0, refund: fee?.refund ?? 0 })
	},
})

const rows = computed<BreakdownRow[]>(() => {
	const b = breakdown.value
	const total: BreakdownRow =
		refundToday.value > 0
			? { label: 'Your refund', value: money(refundToday.value), emphasis: true }
			: { label: 'To end today', value: money(q.value.endToday?.amount), emphasis: true }
	if (!b) return [total]
	return [
		{
			label: `${pluralize(b.shippedMonths, 'month')} received × ${money(b.rate)}`,
			value: money(b.shippedMonths * b.rate),
		},
		{ label: `${pluralize(b.paidMonths, 'month')} already paid`, value: `−${money(b.paid)}` },
		total,
	]
})

const consequences = computed(() => {
	const until = q.value.endToday?.access_until
	return [
		'Your next 3-month supply won’t ship',
		'If you restart later, you’ll need a new intake and clinician approval before anything ships',
		...(until ? [`Your clinicians and the Kova app stay available until ${formatDate(until, 'short')}`] : []),
	]
})

async function onPay() {
	// pay-fee has no idempotency key on the backend — one charge per click, never two (the store also
	// shares one request between callers, and `charging` holds the page).
	if (processing.value || !ready.value) return
	processing.value = true
	charging = true
	const amount = q.value.endToday?.amount ?? 0
	const refund = refundToday.value
	const currency = q.value.currency
	const changed = stateChangeOnCancel('hard')
	step.done(['cancel_and_pay'])
	const closeProcessing = processingPaymentStep()

	function toReceipt() {
		changed()
		const accessUntil = subs.subscription?.expired_at ?? null
		flow.setOutcome(
			refund > 0
				? { kind: 'refunded', refund, currency, accessUntil }
				: { kind: 'plan_ended', amount, currency, accessUntil },
		)
		survey.reset()
		router.replace('/cancel/done')
	}

	try {
		const result = await subs.payFee()
		charging = false
		closeProcessing()
		if (result === 'requires_action') {
			router.replace({ path: COMMITMENT_ROUTES.failed, state: { payment_error: 'requires_3ds', reason_code: null } })
			return
		}
		toReceipt()
	} catch (err) {
		if (err instanceof CancellationNotOfferedError) {
			// Nothing was charged and the buy-out is gone (a charge or shipment moved the numbers):
			// show what is offered now.
			await subs.refreshSubscription()
			charging = false
			closeProcessing()
			router.replace(COMMITMENT_ROUTES.options)
			return
		}
		// A 500 / dropped connection: the charge may have gone through with its answer lost. Re-read before
		// saying anything — a buy-out gone off a cancelled plan is a fee taken, and gets its receipt.
		const unknown = !(err instanceof PaymentDeclinedError)
		if (unknown) await subs.refreshSubscription()
		charging = false
		closeProcessing()
		if (unknown && feeTaken(subs.subscription)) {
			toReceipt()
			return
		}
		// A declined card, or an outcome still unknown after the re-read (owner, 2026-10-13: show "Payment
		// unsuccessful" rather than guess). "Try again" comes back here, which re-reads before the next charge.
		// Why it failed rides along for PAYMENT_UNSUCCESSFUL's analytics (`payment_error`). Both keys every time:
		// `replace` merges into the entry's state, so an earlier failure's `reason_code` would otherwise stay.
		const state =
			err instanceof PaymentDeclinedError
				? { payment_error: 'card_declined', reason_code: err.reasonCode ?? null }
				: { payment_error: 'unknown', reason_code: null }
		router.replace({ path: COMMITMENT_ROUTES.failed, state })
	} finally {
		charging = false
		processing.value = false
	}
}
</script>
